import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButtonWithHover";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";
import { Eye, EyeOff } from "lucide-react";
import { closeSignInModal, signin } from "../../store";
import { roles } from "../../constants/commonConstants";

const SignInForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const [doSignin, isSigninIn, signinError] = useThunk(signin);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      email: "",
      password: "",
      rememberMe: false,
    },
    {
      email: true,
      password: true,
      rememberMe: false,
    },
    {
      email: COMMON_FIELD_TYPES.EMAIL_OR_PHONE,
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      rememberMe: COMMON_FIELD_TYPES.BOOLEAN_FIELD,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      email: true,
      password: true,
      rememberMe: true,
    });

    const { rememberMe, ...otherData } = formData;

    if (validateForm()) {
      const result = await doSignin(otherData);

      if (result?.success) {
        showToast("success", "Login Successful");
        navigate("/admin");
      } else {
        showToast("error", result?.error?.message || "Login failed");
      }
    }
  };

  return (
    <div className="relative xl2:h-[550px] w-full my-auto flex flex-col xl2:p-6">
      <h1 className="text-level-4 md:text-level-3 lg:text-level-2 xl2:text-[48px] font-semibold font-poppins">
        <span className="text-black">Sign </span>
        <span className="text-roselea-green-1">in</span>
      </h1>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col justify-center items-center"
      >
        <div className="flex flex-col gap-3 mt-[40px]">
          <CustomInput
            label=""
            type="text"
            name="email"
            value={formData.email}
            error={touched.email && errors.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onBlur={() => handleBlur("email", formData.email)}
            onFocus={() => clearError("email")}
            placeholder="Enter Your Username"
            className="w-[280px] sm:w-[380px] lg:w-[400px] xl2:w-[512px] h-[40px] px-3 rounded-[12px] border border-input-border-roselea-green bg-primary-light/30 text-gray-600"
            labelStyle="font-alexandria font-medium  sm:text-[16px] text-dark"
            inputStyle="bg-input-roselea-green bg-opacity-[0.03]"
            isRequired={true}
          />
          <div className="relative">
            <CustomInput
              label=""
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              error={touched.password && errors.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={() => handleBlur("password", formData.password)}
              onFocus={() => clearError("password")}
              placeholder="Enter your password"
              className="w-[280px] sm:w-[380px] lg:w-[400px] xl2:w-[512px] h-[40px] px-3 rounded-[12px] border border-input-border-roselea-green bg-primary-light/30 text-gray-600"
              labelStyle="font-alexandria font-medium  sm:text-[16px] text-dark"
              inputStyle="bg-input-roselea-green bg-opacity-[0.03]"
              isRequired={true}
            />
            <button
              type="button"
              className="absolute right-4 top-[20px] -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="flex justify-end w-full mt-[6px] xs:mt-3">
          <Link
            to="/forgot-password"
            className="font-medium font-alexandria underline text-roselea-green-1 text-[12px] sm:text-[16px] hover:no-underline"
          >
            Forgot Password?
          </Link>
        </div>

        <div className="flex flex-col gap-2 mt-10 xl2:mt-20">
          <CustomButton
            type="submit"
            className="h-[40px] xl2:h-[60px] w-[280px] sm:w-[380px] lg:w-[400px] xl2:w-[512px] bg-roselea-green-1 text-level-6 xl2:text-[20px] text-[#F7FAFC] font-poppins font-semibold leading-[28px] rounded-xl"
            buttonText="Sign in"
            loading={isSigninIn}
            disabled={isSigninIn}
          />
        </div>
      </form>
    </div>
  );
};

export default SignInForm;
